/**
 * focus-trap.ts — Focus trap utilities for accessible modals.
 *
 * Exports:
 *   - nextFocusIndex: pure helper for Tab/Shift+Tab cycling (node-testable)
 *   - getFocusableElements: DOM query for focusable children
 *   - useFocusTrap: React hook that traps Tab within a container and restores focus on deactivate
 */

/* global HTMLElement, KeyboardEvent, requestAnimationFrame, cancelAnimationFrame */

import { useEffect, useRef } from "react";
import type { RefObject } from "react";

// Selectors that identify keyboard-focusable elements
const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

/**
 * Pure helper — wraps Tab/Shift+Tab cycling through a list of length `length`.
 * @param current  index of the currently-focused element (0-based)
 * @param length   total number of focusable elements
 * @param shift    true when Shift is held (reverse direction)
 * @returns        next index (wraps around)
 */
export function nextFocusIndex(current: number, length: number, shift: boolean): number {
  if (length <= 0) return 0;
  if (shift) {
    return current <= 0 ? length - 1 : current - 1;
  }
  return current >= length - 1 ? 0 : current + 1;
}

/**
 * Returns all currently-focusable descendant elements of `container`.
 * DOM-only — not called from unit tests.
 */
export function getFocusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => !el.closest("[aria-hidden='true']"),
  );
}

/**
 * React hook — traps Tab key focus within `ref` while `active` is true.
 * Restores focus to the element that was focused before activation.
 *
 * Guards all document/window access so the module is safe to import in SSR/node.
 */
export function useFocusTrap(ref: RefObject<HTMLElement | null>, active: boolean): void {
  // Store the element that had focus before the trap was activated
  const previousFocus = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (typeof document === "undefined") return;

    if (active) {
      // Remember who had focus so we can restore it
      previousFocus.current = document.activeElement as HTMLElement | null;

      // Move focus into the container on the next tick (let render settle)
      const frameId = requestAnimationFrame(() => {
        if (!ref.current) return;
        const elements = getFocusableElements(ref.current);
        if (elements.length > 0) {
          elements[0].focus();
        } else {
          ref.current.focus();
        }
      });

      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key !== "Tab" || !ref.current) return;
        const elements = getFocusableElements(ref.current);
        if (elements.length === 0) {
          e.preventDefault();
          return;
        }
        const currentIndex = elements.indexOf(document.activeElement as HTMLElement);
        const next = nextFocusIndex(currentIndex === -1 ? 0 : currentIndex, elements.length, e.shiftKey);
        e.preventDefault();
        elements[next].focus();
      };

      document.addEventListener("keydown", handleKeyDown);

      return () => {
        cancelAnimationFrame(frameId);
        document.removeEventListener("keydown", handleKeyDown);
      };
    } else {
      // Restore focus to previous element when deactivating
      const target = previousFocus.current;
      if (target && typeof target.focus === "function") {
        // Defer so the closing animation can start before refocusing
        requestAnimationFrame(() => target.focus());
      }
      previousFocus.current = null;
    }
  }, [active, ref]);
}
